import { Button, Card, Form, Select, Space, Typography } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import type { Customer, ShipVo } from "@zhihaoscm/entity";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Grid as ZhGrid, GridItem as ZhGridItem } from "zhihao-ui-react";

import request from "@/api";
import { DEFAULT_VALUE } from "@/config";
import { useUserStore } from "@/stores/react/authStore";

export interface InstallPromotionRecordFormPageProps {
    loadShips?: (keyword?: string) => Promise<ShipVo[]>;
    loadPromoters?: (keyword?: string) => Promise<Customer[]>;
    createRecord?: (form: {
        ship: Record<string, unknown>;
        carrier: Record<string, unknown>;
        promoter: Record<string, unknown>;
    }) => Promise<void>;
    permissions?: string[];
}

/** 复用 Vue 安装推广船舶选择器。 */
export async function loadInstallPromotionShips(keyword = ""): Promise<ShipVo[]> {
    const response = await request.service.get<{ list: ShipVo[] }>("/ship/install/selector", {
        params: { searchParam: keyword }
    });
    return response.data.list || [];
}

/** 复用 Vue 推介人客户选择器。 */
export async function loadInstallPromotionPromoters(keyword = ""): Promise<Customer[]> {
    const response = await request.service.get<Customer[]>("/customer/selector", {
        params: { param: keyword, personalAuth: 1 }
    });
    return response.data || [];
}

/** 复用 Vue 安装推广记录创建接口。 */
export async function createInstallPromotionRecord(form: {
    ship: Record<string, unknown>;
    carrier: Record<string, unknown>;
    promoter: Record<string, unknown>;
}): Promise<void> {
    await request.service.post("/install/promotion/record", form);
}

/** 物流管理-安装推广记录新增表单，覆盖 Vue 船舶/推介人选择和创建 payload。 */
export function InstallPromotionRecordFormPage({
    loadShips = loadInstallPromotionShips,
    loadPromoters = loadInstallPromotionPromoters,
    createRecord = createInstallPromotionRecord,
    permissions
}: InstallPromotionRecordFormPageProps) {
    const navigate = useNavigate();
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const canWrite =
        permissions === undefined ||
        permissions.includes("ROLE_DEVICE_RECORD_W") ||
        storePermissions.includes("ROLE_DEVICE_RECORD_W");
    const [ships, setShips] = useState<ShipVo[]>([]);
    const [promoters, setPromoters] = useState<Customer[]>([]);
    const [shipId, setShipId] = useState("");
    const [promoterId, setPromoterId] = useState("");
    const [shipError, setShipError] = useState("");
    const [promoterError, setPromoterError] = useState("");
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [shipLoading, setShipLoading] = useState(false);
    const [promoterLoading, setPromoterLoading] = useState(false);

    /** 旧 Vue 选择器在输入关键字时远程刷新候选船舶。 */
    const searchShips = async (keyword = "") => {
        setShipLoading(true);
        try {
            setShips(await loadShips(keyword));
        } catch {
            setErrorMessage("安装推广表单加载失败");
        } finally {
            setShipLoading(false);
        }
    };

    /** 旧 Vue 选择器在输入关键字时远程刷新个人认证客户。 */
    const searchPromoters = async (keyword = "") => {
        setPromoterLoading(true);
        try {
            setPromoters(await loadPromoters(keyword));
        } catch {
            setErrorMessage("安装推广表单加载失败");
        } finally {
            setPromoterLoading(false);
        }
    };

    useEffect(() => {
        void searchShips();
        void searchPromoters();
        // 加载函数由路由页面或测试注入，仅在其身份变化时刷新初始候选项。
    }, [loadPromoters, loadShips]);

    const submit = async () => {
        const nextShipError = shipId ? "" : "请选择船舶";
        const nextPromoterError = promoterId ? "" : "请选择推介人账号";
        setShipError(nextShipError);
        setPromoterError(nextPromoterError);
        if (nextShipError || nextPromoterError) return;
        const shipVo = ships.find(item => String(item.ship.id) === shipId);
        const promoter = promoters.find(item => String(item.id) === promoterId);
        if (!shipVo || !promoter) return;
        try {
            await createRecord({
                ship: {
                    id: Number(shipVo.ship.id),
                    name: shipVo.ship.name,
                    cnname: shipVo.ship.cnname,
                    mmsi: shipVo.ship.id
                },
                carrier: {
                    id: Number(shipVo.customer?.id),
                    realName: shipVo.customer?.realName,
                    institutionName: shipVo.customer?.institutionName,
                    mobile: shipVo.customer?.mobile,
                    code: shipVo.customer?.code
                },
                promoter: {
                    id: Number(promoter.id),
                    realName: promoter.realName,
                    institutionName: promoter.institutionName,
                    mobile: promoter.mobile,
                    code: promoter.code
                }
            });
            navigate(-1);
        } catch {
            setErrorMessage("安装推广记录创建失败");
        }
    };

    return (
        <section aria-label="install-promotion-record-form-page" className="flex min-h-full flex-col">
            <Card>
                <Typography.Title level={4}>新增安装推广记录</Typography.Title>
                {errorMessage ? <Alert className="mb-4" message={errorMessage} role="alert" type="error" /> : null}
                <Form labelAlign="right" layout="horizontal" onFinish={() => void submit()}>
                    <ZhGrid cols={4} gap={[30, 0]} className="mb-2">
                        <Form.Item
                            label="关联船舶"
                            validateStatus={shipError ? "error" : undefined}
                            help={shipError || undefined}
                        >
                            <Select<string>
                                aria-label="关联船舶"
                                allowClear
                                filterOption={false}
                                loading={shipLoading}
                                options={ships.map(item => ({
                                    value: String(item.ship.id),
                                    label: `${item.ship.cnname || DEFAULT_VALUE}/${item.ship.name || DEFAULT_VALUE}`
                                }))}
                                placeholder="请搜索船舶名"
                                showSearch
                                value={shipId}
                                onSearch={keyword => void searchShips(keyword)}
                                onChange={value => {
                                    setShipId(value || "");
                                    setShipError("");
                                }}
                            />
                        </Form.Item>
                        <ZhGridItem span={3}>
                            {shipId ? (
                                <Form.Item label="承运商账号">
                                    {(() => {
                                        const ship = ships.find(item => String(item.ship.id) === shipId);
                                        return ship?.customer
                                            ? `${ship.customer.institutionName || ship.customer.realName || DEFAULT_VALUE} | ${ship.customer.mobile || DEFAULT_VALUE} | ${ship.customer.code || DEFAULT_VALUE}`
                                            : DEFAULT_VALUE;
                                    })()}
                                </Form.Item>
                            ) : null}
                        </ZhGridItem>
                    </ZhGrid>
                    <ZhGrid cols={4} gap={[30, 0]} className="mb-2">
                        <Form.Item
                            label="推介人账号"
                            validateStatus={promoterError ? "error" : undefined}
                            help={promoterError || undefined}
                        >
                            <Select<string>
                                aria-label="推介人账号"
                                allowClear
                                filterOption={false}
                                loading={promoterLoading}
                                options={promoters.map(item => ({
                                    value: String(item.id),
                                    label: `${item.realName || item.institutionName || DEFAULT_VALUE} | ${item.mobile || DEFAULT_VALUE} | ${item.code || DEFAULT_VALUE}`
                                }))}
                                placeholder="请搜索推介人账号信息"
                                showSearch
                                value={promoterId}
                                onSearch={keyword => void searchPromoters(keyword)}
                                onChange={value => {
                                    setPromoterId(value || "");
                                    setPromoterError("");
                                }}
                            />
                        </Form.Item>
                    </ZhGrid>
                    <Space className="operation-fixed-btn" size={0}>
                        <Button aria-label="提交" disabled={!canWrite} htmlType="submit" size="large" type="primary">
                            提交
                        </Button>
                        <Button className="transp-btn" size="large" type="primary" onClick={() => navigate(-1)}>
                            返回
                        </Button>
                    </Space>
                </Form>
            </Card>
        </section>
    );
}
